import type { DonationStatus } from './donation-status.js';
import {
  extractReferenceCodes,
  foldReferenceCodeText,
  isValidReferenceCode,
  REFERENCE_CODE_LENGTH,
} from './reference-code.js';

/**
 * Evidence of a fulfilment that arrives by itself — a photo or a video a field
 * team sends — and is read for the donation it shows. It is `inbound_media`,
 * docs/plans/05-finance-reconciliation.md §5.9. **Reading it never moves a
 * donation's status and never sets `notified_at`**: the legacy completed a
 * donation when its last video arrived, and v2 keeps the human `fulfill`.
 */
export const INBOUND_MEDIA_SOURCES = ['whatsapp', 'telegram', 'email', 'upload'] as const;
export type InboundMediaSource = (typeof INBOUND_MEDIA_SOURCES)[number];

export const INBOUND_MEDIA_KINDS = ['image', 'video'] as const;
export type InboundMediaKind = (typeof INBOUND_MEDIA_KINDS)[number];

export const INBOUND_MEDIA_STATUSES = [
  'received',
  'decoding',
  'matched',
  'unreadable',
  'donation_not_found',
  'donation_not_eligible',
  'update_failed',
] as const;
export type InboundMediaStatus = (typeof INBOUND_MEDIA_STATUSES)[number];

/**
 * The outcomes a person works by hand. `update_failed` is the automatic pass
 * running out of attempts before it could record any other outcome — the file
 * could not be fetched, or storage, the scanner, the OCR provider or the
 * database stayed away through every retry.
 */
export const INBOUND_MEDIA_UNMATCHED_STATUSES = [
  'unreadable',
  'donation_not_found',
  'donation_not_eligible',
  'update_failed',
] as const satisfies readonly InboundMediaStatus[];

/** Still in the automatic pass: fetched or not, scanned or not, being read. */
export const INBOUND_MEDIA_IN_FLIGHT_STATUSES = [
  'received',
  'decoding',
] as const satisfies readonly InboundMediaStatus[];

export const INBOUND_MEDIA_METHODS = ['qr', 'ocr'] as const;
export type InboundMediaMethod = (typeof INBOUND_MEDIA_METHODS)[number];

export const INBOUND_MEDIA_MATCH_TYPES = ['auto', 'manual'] as const;
export type InboundMediaMatchType = (typeof INBOUND_MEDIA_MATCH_TYPES)[number];

/** A donation evidence may be attached to: on the books, in its queue, or already carried out. */
export const EVIDENCE_ELIGIBLE_DONATION_STATUSES = [
  'pending',
  'queued',
  'fulfilled',
] as const satisfies readonly DonationStatus[];

/** The donations a field team still has to carry out — what evidence arriving hands to a person. */
export const AWAITING_FULFILMENT_DONATION_STATUSES = [
  'pending',
  'queued',
] as const satisfies readonly DonationStatus[];

const INELIGIBLE_STATUS_REASONS = {
  cancelled: 'INBOUND_MEDIA_DONATION_CANCELLED',
} as const satisfies Record<
  Exclude<DonationStatus, (typeof EVIDENCE_ELIGIBLE_DONATION_STATUSES)[number]>,
  string
>;

/**
 * Why a medium was not matched to a donation in a status docs/decisions/0077
 * retired. No pass writes these any more, but a medium refused before the
 * migration keeps its `failure_reason`, and the screen still reads it.
 */
const RETIRED_INELIGIBLE_STATUS_REASONS = [
  'INBOUND_MEDIA_DONATION_DRAFT',
  'INBOUND_MEDIA_DONATION_PENDING_APPROVAL',
  'INBOUND_MEDIA_DONATION_REJECTED',
] as const;

/**
 * Why the automatic pass did not match a medium, as the row's `failure_reason`.
 * `update_failed` carries the worker's classified error code instead, which is
 * not a sentence a person reads — the screen says the pass could not finish.
 */
export const INBOUND_MEDIA_FAILURE_REASONS = [
  'INBOUND_MEDIA_NO_CODE',
  'INBOUND_MEDIA_SEVERAL_CODES',
  'INBOUND_MEDIA_UNSUPPORTED',
  'INBOUND_MEDIA_SENDER_UNAUTHORIZED',
  'INBOUND_MEDIA_TOO_LARGE',
  'INBOUND_MEDIA_DAMAGED',
  'INBOUND_MEDIA_BUDGET_EXCEEDED',
  'INBOUND_MEDIA_INFECTED',
  'INBOUND_MEDIA_SCAN_FAILED',
  'INBOUND_MEDIA_MISSING',
  'INBOUND_MEDIA_DONATION_NOT_FOUND',
  ...Object.values(INELIGIBLE_STATUS_REASONS),
  ...RETIRED_INELIGIBLE_STATUS_REASONS,
  'INBOUND_MEDIA_SHARE_OUT_OF_RANGE',
] as const;
export type InboundMediaFailureReason = (typeof INBOUND_MEDIA_FAILURE_REASONS)[number];

/**
 * Every bound on untrusted media, in one place. A change is a code change with
 * its test, not configuration — as the statement readers' bounds are
 * (docs/decisions/0047-a-statement-file-is-read-by-bounded-readers-in-core.md).
 *
 * - **Bytes.** An image fits one OCR request once base64-encoded (Vision's
 *   request limit is 10 MB), and a video stays under clamd's default 25 MiB
 *   stream limit, so the scan can always give a verdict.
 * - **Dimensions.** Read from the header before any decoder allocates a pixel:
 *   8192 on a side and 16.7 megapixels, which a phone photo sent over WhatsApp
 *   never reaches, and whose RGBA buffer is 64 MiB.
 * - **Frames.** Thirty spread over a video for the QR reader, as the legacy
 *   read, scaled to 1280 on the long side; at most eight of those — and one for
 *   an image — may be sent to OCR, and only when no QR code was found.
 * - **Time.** Ten minutes of video, and ninety seconds of wall clock for the
 *   whole read, OCR included.
 */
export const INBOUND_MEDIA_LIMITS = {
  imageMaxBytes: 7 * 1024 * 1024,
  videoMaxBytes: 24 * 1024 * 1024,
  imageMaxSide: 8192,
  imageMaxPixels: 16_777_216,
  videoMaxSeconds: 600,
  videoMaxSide: 8192,
  maxFrames: 30,
  maxOcrFrames: 8,
  frameMaxSide: 1280,
  decodeBudgetMs: 90_000,
} as const;

/** The most a download may read before the bytes say what they are. */
export const INBOUND_MEDIA_MAX_BYTES = Math.max(
  INBOUND_MEDIA_LIMITS.imageMaxBytes,
  INBOUND_MEDIA_LIMITS.videoMaxBytes,
);

export function inboundMediaMaxBytes(kind: InboundMediaKind): number {
  return kind === 'image' ? INBOUND_MEDIA_LIMITS.imageMaxBytes : INBOUND_MEDIA_LIMITS.videoMaxBytes;
}

/** A share of a multi-share donation, as a printed sheet numbers it. */
export const INBOUND_MEDIA_MAX_SHARE_INDEX = 999;

/** What a medium names: a donation, and which of its shares when it says so. */
export type EvidenceCode = { referenceCode: string; shareIndex: number | null };

const QR_PAYLOAD_MAX_LENGTH = 64;
const SHARE_SUFFIX = /^(.+)\|\s*([1-9][0-9]{0,2})$/;
const CODE_SYMBOLS = /^[0-9A-Z]+$/;

function wholeCode(text: string): string | null {
  const folded = foldReferenceCodeText(text);
  if (folded.length !== REFERENCE_CODE_LENGTH || !CODE_SYMBOLS.test(folded)) return null;
  return isValidReferenceCode(folded) ? folded : null;
}

/**
 * A QR payload BTSis printed: the reference code alone — in any case, with or
 * without its display dashes — or the code, `|` and a share number, the
 * legacy's `kod|sira`. **Nothing else resolves**: not a URL that contains a
 * code, not a numeric id, not a code with a symbol a check refuses. A QR code
 * carries its own error correction, so a payload is never corrected here, and
 * the tokenizer's rule holds — a character outside the fold ends the reading
 * rather than being stepped over.
 */
export function readEvidenceQrPayload(payload: string): EvidenceCode | null {
  const trimmed = payload.trim();
  if (trimmed.length === 0 || trimmed.length > QR_PAYLOAD_MAX_LENGTH) return null;

  const code = wholeCode(trimmed);
  if (code) return { referenceCode: code, shareIndex: null };

  const shared = SHARE_SUFFIX.exec(trimmed);
  if (!shared) return null;
  const sharedCode = wholeCode(shared[1] ?? '');
  if (!sharedCode) return null;
  return { referenceCode: sharedCode, shareIndex: Number.parseInt(shared[2] ?? '', 10) };
}

/**
 * What a printout's QR code carries: the reference code alone, as stored — no
 * display dashes, no share, no URL — the first form `readEvidenceQrPayload`
 * resolves. A page is one donation, so it names no share; and a URL would
 * resolve to nothing, on purpose. Refuses a code that is not a valid one rather
 * than printing a symbol the decoder would read as no code at all.
 */
export function evidenceQrPayload(referenceCode: string): string {
  const code = wholeCode(referenceCode);
  if (code === null) throw new Error('a printout QR code names a valid reference code');
  return code;
}

/** The longest OCR text read for codes; a frame's text is a sign, not a book. */
export const OCR_TEXT_MAX_LENGTH = 20_000;

/**
 * Every valid code in the text an OCR pass returned, through the same reader a
 * bank description goes through — so a lookalike character or a punctuation
 * mark inside a code ends it, and a code with one wrong symbol is not a code.
 * No share is read from free text.
 */
export function readEvidenceText(text: string): EvidenceCode[] {
  return extractReferenceCodes(text.slice(0, OCR_TEXT_MAX_LENGTH)).map((referenceCode) => ({
    referenceCode,
    shareIndex: null,
  }));
}

export type EvidenceDecision =
  | { kind: 'none' }
  | { kind: 'one'; code: EvidenceCode }
  | { kind: 'several'; referenceCodes: string[] };

/**
 * What a medium names, across every frame read. One donation, seen in any
 * number of frames, is one code; two different donations are ambiguous and
 * never guessed between. A donation named with two different shares keeps the
 * donation and drops the share, which a person can set by hand.
 */
export function decideEvidence(codes: readonly EvidenceCode[]): EvidenceDecision {
  const shares = new Map<string, Set<number | null>>();
  for (const code of codes) {
    const seen = shares.get(code.referenceCode) ?? new Set<number | null>();
    seen.add(code.shareIndex);
    shares.set(code.referenceCode, seen);
  }
  if (shares.size === 0) return { kind: 'none' };
  if (shares.size > 1) return { kind: 'several', referenceCodes: [...shares.keys()] };
  const [[referenceCode, seen]] = [...shares.entries()] as [[string, Set<number | null>]];
  const named = [...seen].filter((share): share is number => share !== null);
  return {
    kind: 'one',
    code: { referenceCode, shareIndex: named.length === 1 ? (named[0] as number) : null },
  };
}

export type EvidenceEligibility =
  | { eligible: true }
  | { eligible: false; reason: InboundMediaFailureReason };

/**
 * Whether evidence may be attached to the donation a code resolved to. Its
 * status must be one a field team works; a share must be one the donation has —
 * a donation with no quantity has one share. Nothing here moves anything.
 */
export function evidenceEligibility(
  donation: { status: DonationStatus; quantity: number | null },
  shareIndex: number | null,
): EvidenceEligibility {
  const reason = (INELIGIBLE_STATUS_REASONS as Partial<Record<DonationStatus, string>>)[
    donation.status
  ];
  if (reason !== undefined) {
    return { eligible: false, reason: reason as InboundMediaFailureReason };
  }
  if (shareIndex !== null && shareIndex > (donation.quantity ?? 1)) {
    return { eligible: false, reason: 'INBOUND_MEDIA_SHARE_OUT_OF_RANGE' };
  }
  return { eligible: true };
}

/**
 * The frames sent to OCR out of those the QR reader read: evenly spread, the
 * first and the middle of the medium before its ends, at most `budget`.
 */
export function ocrFrameIndexes(frameCount: number, budget: number): number[] {
  if (frameCount <= 0 || budget <= 0) return [];
  if (frameCount <= budget) return Array.from({ length: frameCount }, (_, index) => index);
  const step = frameCount / budget;
  return Array.from({ length: budget }, (_, slot) => Math.floor(step * slot + step / 2));
}
